import { useEffect, useRef, useState } from "react";
import QRCode from "qrcode";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../../firebase";
import { getUser } from "../../utils/sessao";
import { toast } from "../../hooks/useToast";
import { useVerificacaoAutomatica } from "../../hooks/useVerificacaoAutomatica";
import { CommerceDialog } from "../commerce/CommerceDialog";
import { MP_AMBIENTE, gerarPixPdv, cancelarPixPdv, verificarPagamentoPedido, novoRequestId, mensagemMP } from "../../utils/mercadoPago";
import "./pixPdv.css";

/* Pix do Mercado Pago numa venda do PDV. Esta tela NUNCA registra a venda:
   quem faz isso é o servidor, quando o Mercado Pago confirma (pela consulta
   desta tela, pela conferência agendada ou pelo webhook) — então a venda
   entra mesmo se o PDV for fechado. Aqui só acompanhamos
   users/{uid}/pdv_cobrancas/{id}. Fechar sem pagar cancela o Pix. */

const fmt = v => Number(v || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const ENCERRADAS = ["falhou", "estornado", "revisao"];

export function PixMercadoPagoPdv({ venda, emailInicial = "", onPago, onFechar }) {
  const uid = getUser()?.uid;
  const [email, setEmail] = useState(emailInicial);
  const [simular, setSimular] = useState(false);
  const [cobranca, setCobranca] = useState(null);
  const [situacao, setSituacao] = useState(null);
  const [qr, setQr] = useState("");
  const [ocupado, setOcupado] = useState(false);
  const requestId = useRef(novoRequestId());
  const concluido = useRef(false);

  // A venda que o servidor guarda (e registra quando o pagamento confirma)
  useEffect(() => {
    if (!uid || !cobranca?.cobrancaId) return undefined;
    return onSnapshot(doc(db, "users", uid, "pdv_cobrancas", cobranca.cobrancaId),
      s => { const v = s.data()?.situacao; if (v) setSituacao(v); },
      e => console.warn("pdv_cobranca_acompanhamento", e?.code || e?.message));
  }, [uid, cobranca?.cobrancaId]);

  const pago = situacao === "pago";
  const encerrada = ENCERRADAS.includes(situacao);
  useEffect(() => {
    if (pago && !concluido.current) { concluido.current = true; toast("✅ Pix confirmado — venda registrada.", "success"); onPago(); }
  }, [pago, onPago]);

  useEffect(() => {
    let atual = true;
    setQr("");
    if (cobranca?.pix?.codigo) QRCode.toDataURL(cobranca.pix.codigo, { width: 240, margin: 1 }).then(u => { if (atual) setQr(u); }).catch(() => {});
    return () => { atual = false; };
  }, [cobranca]);

  // Tela aberta: confere sozinha, sem depender do aviso (webhook) do Mercado Pago.
  // Usa a própria resposta (não só o acompanhamento pelo Firestore).
  useVerificacaoAutomatica(!!cobranca?.cobrancaId && !pago && !encerrada, async () => {
    const r = await verificarPagamentoPedido(cobranca.cobrancaId);
    if (r?.situacao === "pago" || ENCERRADAS.includes(r?.situacao)) setSituacao(r.situacao);
  });

  const gerar = async e => {
    e.preventDefault();
    setOcupado(true);
    try {
      const r = await gerarPixPdv({ venda, requestId: requestId.current, email: email.trim(), simularAprovacao: simular });
      setCobranca(r);
      setSituacao(r.situacao);
    } catch (err) { toast(mensagemMP(err), "error"); }
    finally { setOcupado(false); }
  };

  const verificar = async () => {
    setOcupado(true);
    try {
      const r = await verificarPagamentoPedido(cobranca.cobrancaId);
      if (r.situacao === "pago") setSituacao("pago");
      else toast(r.situacao === "aguardando" ? "Ainda não consta pagamento no Mercado Pago." : "O Mercado Pago não confirmou este pagamento. Confira na sua conta.", "info");
    } catch (err) { toast(mensagemMP(err), "error"); }
    finally { setOcupado(false); }
  };

  const copiar = async () => {
    try { await navigator.clipboard.writeText(cobranca.pix.codigo); toast("📋 Pix copiado!", "success"); }
    catch { toast("Não foi possível copiar", "error"); }
  };

  // Fechar sem pagar: cancela no Mercado Pago (se já tinha sido pago, a venda entra)
  const fechar = async () => {
    if (ocupado) return;
    if (!cobranca?.cobrancaId || pago || encerrada) { onFechar(); return; }
    setOcupado(true);
    try {
      const r = await cancelarPixPdv(cobranca.cobrancaId);
      if (r.situacao === "pago") { setSituacao("pago"); return; }
      toast("Pix cancelado.", "info");
      onFechar();
    } catch {
      toast("Não foi possível cancelar o Pix agora. Se o cliente pagar nos próximos 30 minutos, a venda é registrada sozinha.", "warning", 7000);
      onFechar();
    } finally { setOcupado(false); }
  };

  return (
    <CommerceDialog className="pix-pdv" title="Pix Mercado Pago" subtitle={`Venda de R$ ${fmt(venda.total)}`} onClose={fechar}
      footer={cobranca && !pago && !encerrada ? <button type="button" className="pix-pdv-btn" onClick={fechar} disabled={ocupado}>Cancelar Pix</button> : null}>
      {!cobranca ? (
        <form className="pix-pdv-corpo" onSubmit={gerar}>
          <label className="pix-pdv-campo">E-mail do cliente
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="cliente@email.com" autoComplete="off" required autoFocus />
            <small>O Mercado Pago exige um e-mail para gerar o Pix.</small>
          </label>
          {MP_AMBIENTE === "teste" && (
            <label className="pix-pdv-simular"><input type="checkbox" checked={simular} onChange={e => setSimular(e.target.checked)} /> Simular aprovação (ambiente de teste)</label>
          )}
          <button type="submit" className="pix-pdv-btn primario" disabled={ocupado || !email.trim()}>{ocupado ? "Gerando…" : `Gerar Pix de R$ ${fmt(venda.total)}`}</button>
        </form>
      ) : (
        <div className="pix-pdv-corpo">
          {qr ? <img className="pix-pdv-qr" src={qr} alt="QR Code do Pix" /> : <div className="pix-pdv-qr pix-pdv-qr--vazio" aria-hidden="true" />}
          <button type="button" className="pix-pdv-btn" onClick={copiar} disabled={!cobranca.pix?.codigo}>📋 Copiar código Pix</button>
          <p className={`pix-pdv-status${pago ? " ok" : encerrada ? " erro" : ""}`} role="status">
            {pago ? "✅ Pago — venda registrada."
              : encerrada ? "Este Pix não pode mais ser pago. Feche e gere outro."
              : "⏳ Aguardando o pagamento — confirma sozinho, e a venda entra mesmo se você fechar o PDV."}
          </p>
          {!pago && !encerrada && <button type="button" className="pix-pdv-link" onClick={verificar} disabled={ocupado}>{ocupado ? "Consultando…" : "Verificar pagamento agora"}</button>}
        </div>
      )}
    </CommerceDialog>
  );
}
